import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import noDataIcon from '../../assets/images/no_data.png';
import { formatCurrency } from '@utils/common';
import {
  isStickyFirstColumn,
  STICKY_FIRST_COLUMN_STYLES,
  STICKY_FIRST_ROW_STYLES,
} from './tableDataUtil';

export type Column<T> = {
  key: keyof T;
  label: string;
  render?: (value: T[keyof T], row: T, level?: number) => ReactNode;
  renderHeader?: (column: Column<T>) => ReactNode;
  secondaryColumns?: Column<T>[];
  customCellStyles?: any;
  customHeaderStyles?: any;
};

export type Row<T> = T & {
  id: string;
  children?: Row<T>[];
  getChildren?: (rowId: string) => Row<T>[];
};

export type RowAction = {
  label?: string;
  icon?: string;
  renderUpdateComponent?: any;
  condition?: (rowId: string) => boolean;
  action?: (rowId: string, el?: any) => void;
  component?: ReactElement;
};

export type GenericTableProps<T> = {
  data: Row<T>[];
  columns: Column<T>[];
  meta: any;
  actions?: RowAction[];
  isSubLevelAction?: boolean;
  onViewRow?: (row: Row<T>) => void;
  isViewRowClickable?: (row: Row<T>) => boolean;
  isLoading?: boolean;
  depth?: number | null;
  showTotal?: boolean;
  customCurrency?: string;
  showCurrencyCode?: boolean;
  getCellBackground?: (value: T[keyof T], cellType: string, row?: Row<T>) => string;
  initiallyExpanded?: boolean;
  isHeightRestricted?: boolean;
  hideExpandIfNoChildren?: boolean;
  alignActionsColumn?: 'left' | 'right' | 'center';
  renderCellPopover?: (cellData: any, row: Row<T>, columnKey: keyof T) => ReactNode;
  isCellPopoverEnabled?: (cellData: any, row: Row<T>, columnKey: keyof T) => boolean;
  enableCopyData?: boolean;
  isHeaderVisible?: boolean;
  loaderRowCount?: number;
  /** Enables native text selection on cells. Automatically disabled when `enableCopyData` is active or a drag selection is in progress. */
  allowTextSelection?: boolean;
  /** Keeps the first data column fixed while scrolling horizontally. Not supported with MULTI_LEVEL_TABLE chart type. */
  stickyFirstColumn?: boolean;
  rowEditMenuAnchor?: HTMLElement | null;

  onCloseRowEditMenu?: () => void;

  renderRowEditMenu?: () => React.ReactNode;
};

const ROW_COLORS = ['#D8DBDD', '#FFFFFF', '#F0F0F1'];

function ImmutableDataTable<T>({
  data,
  columns,
  meta,
  actions,
  isSubLevelAction,
  onViewRow,
  isViewRowClickable,
  isLoading,
  depth,
  showTotal = false,
  customCurrency = 'USD',
  showCurrencyCode = false,
  getCellBackground,
  initiallyExpanded = false,
  isHeightRestricted = false,
  hideExpandIfNoChildren = false,
  alignActionsColumn = 'center',
  renderCellPopover,
  isCellPopoverEnabled,
  enableCopyData = false,
  isHeaderVisible = true,
  loaderRowCount = 5,
  allowTextSelection = false,
  stickyFirstColumn = false,
  rowEditMenuAnchor,
  onCloseRowEditMenu,
  renderRowEditMenu,
}: GenericTableProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState<string | null>(null);
  const [dynamicNestedRows, setDynamicNestedRows] = useState<{
    index: number;
    dynamicRows: Row<T>[] | null;
  }>({ index: -1, dynamicRows: null });
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverData, setPopoverData] = useState<{
    cellData: any;
    row: Row<T> | null;
    columnKey: keyof T | null;
  }>({ cellData: null, row: null, columnKey: null });
  const [selectedCell, setSelectedCell] = useState<{
    rowId: string;
    columnKey: keyof T;
  } | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{
    anchorOrigin: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' | 'center' };
    transformOrigin: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' | 'center' };
  }>({
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  });

  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartCell, setDragStartCell] = useState<{
    rowId: string;
    columnKey: keyof T;
  } | null>(null);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [copiedCells, setCopiedCells] = useState<Set<string>>(new Set());
  const [mouseDownPos, setMouseDownPos] = useState<{
    x: number;
    y: number;
    rowId: string;
    columnKey: keyof T;
  } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const DRAG_THRESHOLD = 5; // pixels - minimum movement to trigger drag

  const isAllRowsExpanded = (rows: Row<T>[]): boolean => {
    for (const row of rows) {
      if (row.children) {
        if (!expandedRows[row.id] || !isAllRowsExpanded(row.children)) return false;
      }
    }
    return true;
  };

  // Helper function to check if any column has secondary columns
  const hasSecondaryColumns = useMemo(() => {
    return columns.some((col) => col.secondaryColumns && col.secondaryColumns.length > 0);
  }, [columns]);

  // Helper function to get all leaf columns (flattened columns including secondary columns)
  const getLeafColumns = useMemo(() => {
    const leafColumns: Column<T>[] = [];
    columns.forEach((col) => {
      if (col.secondaryColumns && col.secondaryColumns.length > 0) {
        leafColumns.push(...col.secondaryColumns);
      } else {
        leafColumns.push(col);
      }
    });
    return leafColumns;
  }, [columns]);

  useEffect(() => {
    const allExpanded = isAllRowsExpanded(data);
    setAllExpanded(allExpanded);
  }, [expandedRows, data]);

  // Mouse event listeners for drag selection
  useEffect(() => {
    const handleMouseUp = () => {
      setIsDragging(false);
      setDragStartCell(null);
      setMouseDownPos(null);
    };

    if (isDragging || mouseDownPos) {
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, mouseDownPos]);

  // Clear selection when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectedCells.size === 0 && copiedCells.size === 0) return;

      // Check if click is inside the table container
      if (tableContainerRef.current && !tableContainerRef.current.contains(e.target as Node)) {
        setSelectedCells(new Set());
        setCopiedCells(new Set());
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedCells, copiedCells]);

  // Effect to expand all rows initially if initiallyExpanded is true
  useEffect(() => {
    if (initiallyExpanded && data.length > 0) {
      const updatedExpandedRows: Record<string, boolean> = {};

      const setAllRowState = (rows: Row<T>[]) => {
        rows.forEach((row) => {
          if (row.children && row.children.length > 0) {
            updatedExpandedRows[row.id] = true;
            setAllRowState(row.children);
          }
        });
      };

      setAllRowState(data);
      setExpandedRows(updatedExpandedRows);
      setAllExpanded(true);
    }
  }, [initiallyExpanded, data]);

  const isShowActionColumn = Boolean(actions?.length);

  const columnMetadata = meta?.columns?.length
    ? meta.columns.reduce((obj: any, item: any) => {
        obj[item.id] = item;
        return obj;
      }, {})
    : [];

  const handleExpandAllRows = () => {
    const newExpandState = !allExpanded;
    setAllExpanded(newExpandState);

    const updatedExpandedRows: Record<string, boolean> = {};
    const setAllRowState = (rows: Row<T>[]) => {
      rows.forEach((row) => {
        updatedExpandedRows[row.id] = newExpandState;
        if (row.children) {
          setAllRowState(row.children);
        }
      });
    };
    setAllRowState(data);

    setExpandedRows(updatedExpandedRows);
  };

  const handleExpandRowClick = (id: string) => {
    setExpandedRows((prev) => {
      const isExpanded = !prev[id];
      return { ...prev, [id]: isExpanded };
    });
  };

  const handleExpandDynamicChildrenRowClick = async (rowIndex: number, row: any, e: any) => {
    e.stopPropagation();
    if (
      dynamicNestedRows?.dynamicRows?.length &&
      (dynamicNestedRows.dynamicRows[0].id as string).includes(row.id)
    ) {
      setDynamicNestedRows({ index: -1, dynamicRows: null });
      return;
    }
    setDynamicNestedRows({ index: -1, dynamicRows: null });

    if (row.getChildren) {
      const childRows = await row.getChildren(row.id);
      setDynamicNestedRows({
        index: rowIndex,
        dynamicRows: childRows,
      });
    }
  };

  const handleRowActionClick = (rowId: string, action: RowAction, el?: any) => {
    if (!action || !isShowActionColumn) return;

    const { renderUpdateComponent, action: actionHandler } = action;

    if (renderUpdateComponent) {
      setUpdatingRowId((prev) => (prev === rowId ? null : rowId));
    } else if (actionHandler) {
      if (el) {
        actionHandler(rowId, el);
      } else {
        actionHandler(rowId);
      }
    }
  };

  const handleCellClick = (
    event: React.MouseEvent<HTMLElement>,
    cellData: any,
    row: Row<T>,
    columnKey: keyof T,
  ) => {
    if (!renderCellPopover) return;

    // Check if this specific cell is enabled for popover
    if (isCellPopoverEnabled && !isCellPopoverEnabled(cellData, row, columnKey)) return;
    event.stopPropagation();

    const cellElement = event.currentTarget;
    const cellRect = cellElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate available space in each direction
    const spaceRight = viewportWidth - cellRect.right;
    const spaceLeft = cellRect.left;
    const spaceBelow = viewportHeight - cellRect.bottom;
    const spaceAbove = cellRect.top;

    // Determine optimal horizontal position
    let horizontal: 'left' | 'right' | 'center' = 'right';
    let transformHorizontal: 'left' | 'right' | 'center' = 'left';

    // If more space on left, open to the left
    if (spaceLeft > spaceRight && spaceLeft > 400) {
      horizontal = 'left';
      transformHorizontal = 'right';
    } else if (spaceRight > 400) {
      horizontal = 'right';
      transformHorizontal = 'left';
    } else {
      // Center if not enough space on either side
      horizontal = 'center';
      transformHorizontal = 'center';
    }

    // Determine optimal vertical position
    let vertical: 'top' | 'bottom' = 'bottom';
    let transformVertical: 'top' | 'bottom' = 'top';

    // If more space above, open above
    if (spaceAbove > spaceBelow && spaceAbove > 300) {
      vertical = 'top';
      transformVertical = 'bottom';
    }

    setPopoverPosition({
      anchorOrigin: { vertical, horizontal },
      transformOrigin: { vertical: transformVertical, horizontal: transformHorizontal },
    });

    setPopoverAnchor(cellElement);
    setPopoverData({ cellData, row, columnKey });
    setSelectedCell({ rowId: row.id, columnKey });
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverData({ cellData: null, row: null, columnKey: null });
    setSelectedCell(null);
  };

  // Helper function to get cell key
  const getCellKey = (rowId: string, columnKey: keyof T): string => {
    return `${rowId}|||${String(columnKey)}`;
  };

  // Helper function to get all rows in flat structure
  const getFlatRows = useCallback(
    (rows: Row<T>[]): Row<T>[] => {
      const flatRows: Row<T>[] = [];
      const traverse = (rowList: Row<T>[]) => {
        rowList.forEach((row) => {
          flatRows.push(row);
          if (row.children && expandedRows[row.id]) {
            traverse(row.children);
          }
        });
      };
      traverse(rows);
      return flatRows;
    },
    [expandedRows],
  );

  // Handle cell mouse down (start drag selection)
  const handleCellMouseDown = (e: React.MouseEvent, row: Row<T>, columnKey: keyof T) => {
    if (!enableCopyData) return;

    const isCellClickable =
      renderCellPopover &&
      isCellPopoverEnabled &&
      isCellPopoverEnabled(row[columnKey], row, columnKey);

    if (isCellClickable) {
      // For clickable cells: Track mouse position and wait to determine if it's a click or drag
      setMouseDownPos({
        x: e.clientX,
        y: e.clientY,
        rowId: row.id,
        columnKey,
      });
    } else {
      // For non-clickable cells: Start selection immediately (fast response)
      e.preventDefault();
      setIsDragging(true);
      setDragStartCell({ rowId: row.id, columnKey });

      // Clear previous selection and select current cell
      const cellKey = getCellKey(row.id, columnKey);
      setSelectedCells(new Set([cellKey]));
    }
  };

  // Handle cell mouse enter (during drag)
  const handleCellMouseEnter = (e: React.MouseEvent, row: Row<T>, columnKey: keyof T) => {
    if (!enableCopyData) return;

    // Check if we're tracking a potential drag from a clickable cell
    if (mouseDownPos && !isDragging) {
      const dx = Math.abs(e.clientX - mouseDownPos.x);
      const dy = Math.abs(e.clientY - mouseDownPos.y);

      // If movement exceeds threshold, start drag selection
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
        setIsDragging(true);
        setDragStartCell({ rowId: mouseDownPos.rowId, columnKey: mouseDownPos.columnKey });

        // Select from start to current cell
        const cellKey = getCellKey(mouseDownPos.rowId, mouseDownPos.columnKey);
        setSelectedCells(new Set([cellKey]));
      }
    }

    if (!isDragging || !dragStartCell) return;

    const flatRows = getFlatRows(data);
    const startRowIndex = flatRows.findIndex((r) => r.id === dragStartCell.rowId);
    const endRowIndex = flatRows.findIndex((r) => r.id === row.id);
    const startColIndex = getLeafColumns.findIndex(
      (c: Column<T>) => c.key === dragStartCell.columnKey,
    );
    const endColIndex = getLeafColumns.findIndex((c: Column<T>) => c.key === columnKey);

    if (startRowIndex === -1 || endRowIndex === -1 || startColIndex === -1 || endColIndex === -1) {
      return;
    }

    // Calculate the rectangular selection
    const minRow = Math.min(startRowIndex, endRowIndex);
    const maxRow = Math.max(startRowIndex, endRowIndex);
    const minCol = Math.min(startColIndex, endColIndex);
    const maxCol = Math.max(startColIndex, endColIndex);

    const newSelectedCells = new Set<string>();
    for (let r = minRow; r <= maxRow; r++) {
      for (let c = minCol; c <= maxCol; c++) {
        const cellKey = getCellKey(flatRows[r].id, getLeafColumns[c].key);
        newSelectedCells.add(cellKey);
      }
    }

    setSelectedCells(newSelectedCells);
  };

  // Copy selected cells to clipboard
  const handleCopySelectedCells = useCallback(() => {
    if (selectedCells.size === 0) return;

    const flatRows = getFlatRows(data);

    // Group cells by row and column
    const cellsByRow = new Map<string, Map<keyof T, any>>();

    selectedCells.forEach((cellKey) => {
      const [rowId, columnKey] = cellKey.split('|||');
      if (!cellsByRow.has(rowId)) {
        cellsByRow.set(rowId, new Map());
      }
      const row = flatRows.find((r) => r.id === rowId);
      if (row) {
        cellsByRow.get(rowId)!.set(columnKey as keyof T, row[columnKey as keyof T]);
      }
    });

    // Build the text content for clipboard
    const rowIds = Array.from(cellsByRow.keys());
    const rowIndexMap = new Map(flatRows.map((r, i) => [r.id, i]));
    const sortedRowIds = rowIds.sort(
      (a, b) => (rowIndexMap.get(a) ?? 0) - (rowIndexMap.get(b) ?? 0),
    );

    const textRows: string[] = [];
    sortedRowIds.forEach((rowId) => {
      const rowCells = cellsByRow.get(rowId)!;
      const columnKeys = Array.from(rowCells.keys());

      // Sort columns by their order in the leaf columns array
      const columnIndexMap = new Map(getLeafColumns.map((c: Column<T>, i: number) => [c.key, i]));
      const sortedColumnKeys = columnKeys.sort(
        (a, b) => (columnIndexMap.get(a as keyof T) ?? 0) - (columnIndexMap.get(b as keyof T) ?? 0),
      );

      const cellValues = sortedColumnKeys.map((key) => {
        const value = rowCells.get(key);
        const columnType = columnMetadata[key]?.type;

        // Format the value based on type
        if (value === null || value === undefined || value === 'null') {
          return 'N/A';
        }

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          const formattedValue = checkCellValueType(columnType, value);
          return formattedValue !== null ? String(formattedValue) : String(value);
        }

        return String(value);
      });

      textRows.push(cellValues.join('\t'));
    });

    const textContent = textRows.join('\n');

    // Copy to clipboard
    navigator.clipboard
      .writeText(textContent)
      .then(() => {
        // Mark cells as copied (for dashed border styling)
        setCopiedCells(new Set(selectedCells));
      })
      .catch((err) => {
        console.error('Failed to copy to clipboard:', err);
      });
  }, [selectedCells, data, getLeafColumns, columnMetadata, getFlatRows]);

  // Keyboard event listener for copy and clear selection
  useEffect(() => {
    if (!enableCopyData) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Copy with Ctrl+C or Cmd+C
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && selectedCells.size > 0) {
        e.preventDefault();
        e.stopPropagation();
        handleCopySelectedCells();
      }
      // Clear selection with Escape key
      else if (e.key === 'Escape' && (selectedCells.size > 0 || copiedCells.size > 0)) {
        setSelectedCells(new Set());
        setCopiedCells(new Set());
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [selectedCells, copiedCells, handleCopySelectedCells, enableCopyData]);

  // Check if a cell is selected
  const isCellSelected = (rowId: string, columnKey: keyof T): boolean => {
    if (!enableCopyData) return false;
    return selectedCells.has(getCellKey(rowId, columnKey));
  };

  // Check if a cell is copied
  const isCellCopied = (rowId: string, columnKey: keyof T): boolean => {
    if (!enableCopyData) return false;
    return copiedCells.has(getCellKey(rowId, columnKey));
  };

  const checkCellValueType = (type: string, value: any) => {
    switch (type) {
      case 'string':
        return value;
      case 'percentage':
        return Number(value) === 0 ? '-' : `${(Number(value) * 100).toFixed(2)}%`;
      case 'currency':
        return Number(value) === 0
          ? '-'
          : formatCurrency(value, false, 2, 2, { currency: customCurrency }, showCurrencyCode);
      case 'date':
        return value;
      default:
        return null;
    }
  };

  const getCellValueAllignment = (type: string) => {
    switch (type) {
      case 'percentage':
      case 'currency':
        return 'right';
      case 'string':
      case 'date':
        return 'left';
      default:
        return 'center';
    }
  };

  const translateIconCondition = (icons: any[], row: any) => {
    if (!icons || !Array.isArray(icons)) return icons;

    return icons.map((icon) => {
      if (icon.type === 'conditional' && typeof icon.condition === 'string') {
        let isMet = false;

        // Pattern 1: !['A', 'B'].includes(row.type)
        const notInPattern = /^!\s*\[(.*)\]\.includes\(row\.(.*)\)$/;
        // Pattern 2: ['A', 'B'].includes(row.type)
        const inPattern = /^\[(.*)\]\.includes\(row\.(.*)\)$/;

        if (notInPattern.test(icon.condition)) {
          const match = icon.condition.match(notInPattern);
          if (match) {
            const values = match[1]
              .replace(/'/g, '')
              .split(',')
              .map((v: string) => v.trim());
            const field = match[2];
            isMet = !values.includes(row[field]);
          }
        } else if (inPattern.test(icon.condition)) {
          const match = icon.condition.match(inPattern);
          if (match) {
            const values = match[1]
              .replace(/'/g, '')
              .split(',')
              .map((v: string) => v.trim());
            const field = match[2];
            isMet = values.includes(row[field]);
          }
        }

        return {
          ...icon,
          condition: isMet,
        };
      }
      return icon;
    });
  };

  const renderCellContent = (row: any, key: any): ReactNode => {
    const value = row[key];
    const columnType = columnMetadata[key]?.type;

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      let renderValue = checkCellValueType(columnType, value);
      const pivotColumnType = meta?.columns?.[2]?.type;

      // Handle pivot table columns where type needs to be inferred from metadata
      if (!renderValue && meta.chartType === 'PIVOT_TABLE') {
        if (pivotColumnType) {
          renderValue = checkCellValueType(pivotColumnType, value);
        }
      }

      const icons = translateIconCondition(columnMetadata[key]?.icons, row);
      let leftIcon = null;
      let rightIcon = null;

      if (icons && Array.isArray(icons)) {
        icons.forEach((icon) => {
          if (icon.type === 'conditional' && Boolean(icon.condition)) {
            if (icon.position === 'left') {
              leftIcon = (
                <img
                  src={require(`../../assets/icons/${icon.location}`)}
                  alt="left_icon"
                  style={{ marginRight: '8px', userSelect: 'none' }}
                />
              );
            } else if (icon.position === 'right') {
              rightIcon = (
                <img
                  src={require(`../../assets/icons/${icon.location}`)}
                  alt="right_icon"
                  style={{ marginLeft: '8px', userSelect: 'none' }}
                />
              );
            }
          }
        });
      }

      if (value === 'null') {
        return (
          <span
            style={{
              width: '100%',
              minWidth: '3rem',
              display: 'flex',
              justifyContent: getCellValueAllignment(columnType),
            }}
          >
            N/A
          </span>
        );
      }

      return (
        <span
          style={{
            width: '100%',
            minWidth: columnType === 'currency' ? '6rem' : '3rem',
            display: 'flex',
            justifyContent: getCellValueAllignment(columnType),
            ...(columnType === 'currency' || pivotColumnType === 'currency'
              ? { whiteSpace: 'nowrap' }
              : {}),
          }}
        >
          {leftIcon}
          {renderValue ?? value}
          {rightIcon}
        </span>
      );
    }

    return (
      <span
        style={{
          width: '100%',
          minWidth: '3rem',
          display: 'flex',
          justifyContent: getCellValueAllignment(columnType),
        }}
      >
        -
      </span>
    );
  };

  const getRowCellBackGround = (index: number) => {
    if (depth && depth === index) {
      return '#FFFFFF';
    } else {
      return ROW_COLORS[index % 2 ? 0 : 1];
    }
  };

  const renderDynamicRows = (dynamicRows: Row<T>[]) => {
    return dynamicRows.map((row) => (
      <React.Fragment key={`${row.id}`}>
        <TableRow style={{ backgroundColor: '#FFFFFF' }}>
          {meta.chartType === 'MULTI_LEVEL_TABLE' && (
            <TableCell sx={{ border: 'none' }}></TableCell>
          )}
          {getLeafColumns.map((column: Column<T>, colindex: number) => (
            <TableCell
              key={String(column.key)}
              sx={{
                paddingY: '0.75rem',
                paddingLeft: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                lineHeight: '1rem',
                color: '#1B1C17',
                border: '1px solid #2F736E1F',
                borderLeft: colindex === 0 ? '1px solid #2F736E1F' : 'none',
                borderRight: '1px solid #2F736E1F',
                backgroundColor: getCellBackground
                  ? getCellBackground(row[column.key], columnMetadata[column.key], row) || '#FFFFFF'
                  : '#FFFFFF',
                borderTopLeftRadius: colindex === 0 ? '8px' : '0px',
                borderBottomLeftRadius: colindex === 0 ? '8px' : '0px',
                borderTopRightRadius: colindex === columns.length - 1 ? '8px' : '0px',
                borderBottomRightRadius: colindex === columns.length - 1 ? '8px' : '0px',
                ...(isStickyFirstColumn(stickyFirstColumn, colindex, meta.chartType) &&
                  STICKY_FIRST_ROW_STYLES),
                ...(column.customCellStyles ? column.customCellStyles : {}),
              }}
            >
              {column.render
                ? column.render(row[column.key], row, 0)
                : renderCellContent(row, column.key)}
            </TableCell>
          ))}
        </TableRow>
      </React.Fragment>
    ));
  };

  const renderRows = (rows: Row<T>[], level = 0): ReactNode => {
    return rows.map((row, rowIndex) => {
      const isExpanded = expandedRows[row.id];
      const isUpdating = updatingRowId === row.id;
      const actionWithRenderUpdate = actions?.find((action) => action?.renderUpdateComponent);
      let isClickable: boolean;
      if (!isViewRowClickable) {
        isClickable = !!onViewRow;
      } else {
        isClickable = !!onViewRow && isViewRowClickable(row);
      }

      const getCellBackgroundColor = (
        isDragSelected: boolean,
        isSelected: boolean | undefined,
        cellValue: any,
        columnKey: string | number | symbol,
      ): string => {
        if (rowIndex === rows.length - 1 && showTotal) {
          return '#E5EEED';
        }
        if (isDragSelected) {
          return '#2F736E2F';
        }
        if (isSelected) {
          return '#2F736E1F';
        }
        if (getCellBackground) {
          return (
            getCellBackground(cellValue, columnMetadata[columnKey], row) ||
            getRowCellBackGround(level)
          );
        }
        return getRowCellBackGround(level);
      };
      if (isUpdating && actionWithRenderUpdate) {
        return (
          <React.Fragment key={`${row.id}_${rowIndex}`}>
            <TableRow
              style={{
                backgroundColor: '#FFFFFF',
                cursor: isClickable ? 'pointer' : 'default',
                border: 'none',
                borderBottom: '1px solid #2F736E1F',
              }}
              onClick={() => {
                if (isClickable && onViewRow) onViewRow(row);
              }}
            >
              {meta.chartType === 'MULTI_LEVEL_TABLE' && (
                <TableCell
                  style={{
                    width: '5px',
                    paddingLeft: `${level * 20 + 10}px`,
                    paddingTop: 0,
                    paddingBottom: 0,
                    backgroundColor: '#2F736E1F',
                    lineHeight: '1rem',
                    paddingRight: '0px',
                    border: 'none',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                  }}
                ></TableCell>
              )}
              {getLeafColumns.map((column, colindex) => {
                const isSelected =
                  renderCellPopover &&
                  selectedCell?.rowId === row.id &&
                  selectedCell?.columnKey === column.key;
                const isCellClickable =
                  renderCellPopover &&
                  (!isCellPopoverEnabled || isCellPopoverEnabled(row[column.key], row, column.key));
                const isDragSelected = isCellSelected(row.id, column.key);
                const isCopied = isCellCopied(row.id, column.key);
                return (
                  <TableCell
                    key={String(column.key)}
                    onClick={(e) => handleCellClick(e, row[column.key], row, column.key)}
                    {...(enableCopyData && {
                      onMouseDown: (e) => handleCellMouseDown(e, row, column.key),
                      onMouseEnter: (e) => handleCellMouseEnter(e, row, column.key),
                    })}
                    sx={{
                      paddingY: '0.75rem',
                      paddingLeft: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      lineHeight: '1rem',
                      color: '#1B1C17',
                      border: 'none',
                      borderLeft: '1px solid #2F736E1F',
                      backgroundColor: isDragSelected
                        ? '#2F736E2F'
                        : isSelected
                          ? '#2F736E1F'
                          : getCellBackground
                            ? getCellBackground(row[column.key], columnMetadata[column.key], row) ||
                              '#2F736E1F'
                            : '#2F736E1F',
                      cursor: isCellClickable ? 'pointer' : 'default',
                      userSelect:
                        enableCopyData || isDragging
                          ? 'none'
                          : allowTextSelection
                            ? 'text'
                            : 'none',
                      ...(isStickyFirstColumn(stickyFirstColumn, colindex, meta.chartType) &&
                        STICKY_FIRST_ROW_STYLES),
                      ...(isCopied && {
                        outline: '2px dashed #2F736E',
                        outlineOffset: '-3px',
                        backgroundColor: '#2F736E2F',
                      }),
                      ...(column.customCellStyles ? column.customCellStyles : {}),
                    }}
                  >
                    {column.render
                      ? column.render(row[column.key], row, level)
                      : renderCellContent(row, column.key)}
                  </TableCell>
                );
              })}
              {isShowActionColumn && level === 0 && (
                <TableCell
                  key={String(`${row.id}_${rowIndex}_actions`)}
                  sx={{
                    paddingY: '0.75rem',
                    border: 'none',
                    borderLeft: '1px solid #2F736E1F',
                    background: '#2F736E1F',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                  }}
                ></TableCell>
              )}
            </TableRow>
            <TableRow key={`selected_${row.id}`}>
              <TableCell
                colSpan={columns.length + 2}
                style={{
                  padding: 0,
                  backgroundColor: '#2F736E1F',
                  borderBottom: 'none',
                  borderRadius: '16px',
                }}
              >
                {actionWithRenderUpdate.renderUpdateComponent(row, () => setUpdatingRowId(null))}
              </TableCell>
            </TableRow>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={`${row.id}_${rowIndex}`}>
          <TableRow
            style={{
              backgroundColor: '#F0F0F1',
              cursor: isClickable ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (isClickable && onViewRow) onViewRow(row);
            }}
          >
            {meta.chartType === 'MULTI_LEVEL_TABLE' && (
              <TableCell
                style={{
                  width: '5px',
                  paddingLeft: `${level * 20 + 10}px`,
                  paddingTop: 0,
                  paddingBottom: 0,
                  backgroundColor: 'white',
                  borderBottom: 'none',
                  lineHeight: '1rem',
                  paddingRight: '0px',
                }}
              >
                {row.children && !(hideExpandIfNoChildren && row.children.length === 0) && (
                  <button
                    onClick={() => handleExpandRowClick(row.id)}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <ArrowDropDownIcon
                      style={{
                        color: '#162C36',
                        rotate: isExpanded ? '0deg' : '-90deg',
                      }}
                    />
                  </button>
                )}
                {!row.children && row.getChildren && (
                  <button
                    onClick={(e) => handleExpandDynamicChildrenRowClick(rowIndex, row, e)}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <ArrowDropDownIcon
                      style={{
                        color: '#162C36',
                        rotate:
                          dynamicNestedRows?.index === rowIndex || isExpanded ? '0deg' : '-90deg',
                      }}
                    />
                  </button>
                )}
              </TableCell>
            )}
            {getLeafColumns.map((column, colindex) => {
              const isSelected =
                renderCellPopover &&
                selectedCell?.rowId === row.id &&
                selectedCell?.columnKey === column.key;

              let isCellClickable = false;
              if (isClickable) {
                isCellClickable = true;
              } else {
                isCellClickable = Boolean(
                  renderCellPopover &&
                  (!isCellPopoverEnabled || isCellPopoverEnabled(row[column.key], row, column.key)),
                );
              }
              const isDragSelected = isCellSelected(row.id, column.key);
              const isCopied = isCellCopied(row.id, column.key);
              return (
                <TableCell
                  key={String(column.key)}
                  onClick={(e) => handleCellClick(e, row[column.key], row, column.key)}
                  {...(enableCopyData && {
                    onMouseDown: (e) => handleCellMouseDown(e, row, column.key),
                    onMouseEnter: (e) => handleCellMouseEnter(e, row, column.key),
                  })}
                  sx={{
                    paddingY: '0.75rem',
                    paddingLeft: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: rowIndex === rows.length - 1 && showTotal ? 700 : 500,
                    lineHeight: '1rem',
                    color: '#1B1C17',
                    border: 'none',
                    borderTop: '1px solid #737F86',
                    borderBottom: '1px solid #737F86',
                    borderLeft: '1px solid #737F86',
                    borderRight: colindex === columns.length - 1 ? '1px solid #737F86' : 'none',
                    backgroundColor: getCellBackgroundColor(
                      isDragSelected,
                      isSelected,
                      row[column.key],
                      column.key,
                    ),
                    borderTopLeftRadius: rowIndex === 0 && colindex === 0 ? '8px' : '0px',
                    borderBottomLeftRadius:
                      rowIndex === rows.length - 1 && colindex === 0 ? '8px' : '0px',
                    borderTopRightRadius:
                      rowIndex === 0 && colindex === getLeafColumns.length - 1 ? '8px' : '0px',
                    borderBottomRightRadius:
                      rowIndex === rows.length - 1 && colindex === getLeafColumns.length - 1
                        ? '8px'
                        : '0px',
                    cursor: isCellClickable ? 'pointer' : 'default',
                    userSelect:
                      enableCopyData || isDragging ? 'none' : allowTextSelection ? 'text' : 'none',
                    position: 'relative',
                    ...(isStickyFirstColumn(stickyFirstColumn, colindex, meta.chartType) &&
                      STICKY_FIRST_ROW_STYLES),
                    ...(isCopied && {
                      outline: '2px dashed #2F736E',
                      outlineOffset: '-3px',
                      backgroundColor: '#2F736E2F',
                    }),
                    ...(column.customCellStyles ? column.customCellStyles : {}),
                  }}
                >
                  {column.render
                    ? column.render(row[column.key], row, level)
                    : renderCellContent(row, column.key)}
                </TableCell>
              );
            })}
            {isShowActionColumn && (isSubLevelAction ? level !== 0 : level === 0) && (
              <TableCell
                key={String(`${row.id}_${rowIndex}_actions`)}
                sx={{
                  paddingY: '0.75rem',
                  paddingLeft: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: '1rem',
                  border: 'none',
                  background: '#FFFFFF',
                  textAlign: 'left',
                }}
              >
                <Stack gap={'0.5rem'} direction={'row'} alignItems={'center'}>
                  {actions?.map((action, index) => {
                    if (action?.condition && !action.condition(row.id)) {
                      return <></>;
                    } else if (action.component) {
                      return React.cloneElement(action.component, {
                        onClick: (e: Event) => {
                          if (!action.action) return;

                          action.action(row.id, e);
                        },
                      });
                    } else {
                      return (
                        <button
                          key={String(`${row.id}_${rowIndex}_${index}_action`)}
                          onClick={(e) => {
                            handleRowActionClick(row.id, action);
                            e.stopPropagation();
                          }}
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            marginLeft: '0.5rem',
                          }}
                        >
                          {action.icon && (
                            <img
                              src={require(`../../assets/icons/${action.icon}`)}
                              alt="action_icon"
                              style={{ userSelect: 'none' }}
                            />
                          )}
                          <Typography
                            fontSize={'0.875rem'}
                            fontWeight={600}
                            sx={{ color: '#51B8B0' }}
                          >
                            {action.label}
                          </Typography>
                        </button>
                      );
                    }
                  })}
                </Stack>
              </TableCell>
            )}
          </TableRow>
          {isExpanded && row.children && renderRows(row.children, level + 1)}
          {depth && dynamicNestedRows?.index === rowIndex && dynamicNestedRows?.dynamicRows?.length
            ? renderDynamicRows(dynamicNestedRows.dynamicRows)
            : null}
        </React.Fragment>
      );
    });
  };

  if (!data.length) {
    return (
      <TableContainer
        style={{
          width: '100%',
          height: '100%',
          marginBottom: '12px',
        }}
      >
        <Table
          sx={{
            borderSpacing: '0 10px',
            borderCollapse: 'separate',
          }}
        >
          {isHeaderVisible && (
            <TableHead>
              {/* First header row */}
              <TableRow sx={{ borderRadius: '20px' }}>
                {meta?.chartType === 'MULTI_LEVEL_TABLE' && (
                  <TableCell
                    rowSpan={hasSecondaryColumns ? 2 : 1}
                    sx={{
                      padding: 0,
                      paddingLeft: depth === 1 ? '0px' : '5px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      lineHeight: '1rem',
                      borderBottom: 'none',
                      background: '#2F736E',
                      borderTopLeftRadius: '8px',
                      WebkitBorderBottomLeftRadius: '8px',
                      paddingRight: depth === 1 ? '0px' : '1rem',
                    }}
                  >
                    <button
                      onClick={handleExpandAllRows}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        color: '#6C6C6C',
                      }}
                    >
                      <ArrowDropDownIcon
                        style={{
                          color: '#9AD6D1',
                          rotate: allExpanded ? '0deg' : '-90deg',
                        }}
                      />
                    </button>
                  </TableCell>
                )}
                {columns?.map((column, i) => (
                  <TableCell
                    key={String(column.key)}
                    colSpan={column.secondaryColumns?.length || 1}
                    rowSpan={column.secondaryColumns?.length ? 1 : hasSecondaryColumns ? 2 : 1}
                    sx={{
                      paddingY: '0.75rem',
                      paddingLeft: '0.5rem',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      lineHeight: '1rem',
                      color: '#F6F8F9',
                      borderLeft: '1px solid #737F86',
                      borderBottom: 'none',
                      background: '#2F736E',
                      borderTopRightRadius:
                        i === columns.length - 1 && !isShowActionColumn ? '8px' : '0px',
                      borderBottomRightRadius:
                        i === columns.length - 1 && !isShowActionColumn && !hasSecondaryColumns
                          ? '8px'
                          : '0px',
                      borderTopLeftRadius:
                        meta.chartType !== 'MULTI_LEVEL_TABLE' && i === 0 ? '8px' : '0px',
                      WebkitBorderBottomLeftRadius:
                        meta.chartType !== 'MULTI_LEVEL_TABLE' && i === 0 ? '8px' : '0px',
                      textAlign: getCellValueAllignment(columnMetadata[column.key]?.type),
                      ...(isStickyFirstColumn(stickyFirstColumn, i, meta.chartType) &&
                        STICKY_FIRST_COLUMN_STYLES),
                      ...(column.customHeaderStyles ? column.customHeaderStyles : {}),
                    }}
                  >
                    {column.renderHeader ? column.renderHeader(column) : column.label}
                  </TableCell>
                ))}
                {isShowActionColumn && (
                  <TableCell
                    rowSpan={hasSecondaryColumns ? 2 : 1}
                    sx={{
                      paddingY: '0.75rem',
                      paddingLeft: '0.5rem',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      lineHeight: '1rem',
                      color: '#F6F8F9',
                      borderLeft: '1px solid #737F86',
                      borderBottom: 'none',
                      background: '#2F736E',
                      borderTopRightRadius: '8px',
                      borderBottomRightRadius: '8px',
                      textAlign: alignActionsColumn,
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
              {/* Second header row for secondary columns */}
              {hasSecondaryColumns && (
                <TableRow>
                  {columns?.map((column, i) =>
                    column.secondaryColumns?.map((subCol, subIdx) => {
                      const isLastSubCol =
                        i === columns.length - 1 && subIdx === column.secondaryColumns!.length - 1;
                      return (
                        <TableCell
                          key={String(subCol.key)}
                          sx={{
                            paddingY: '0.75rem',
                            paddingLeft: '0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            lineHeight: '1rem',
                            color: '#F6F8F9',
                            borderLeft: '1px solid #737F86',
                            borderBottom: 'none',
                            background: '#2F736E',
                            borderTop: '1px solid #737F86',
                            borderBottomRightRadius:
                              isLastSubCol && !isShowActionColumn ? '8px' : '0px',
                            textAlign: getCellValueAllignment(columnMetadata[subCol.key]?.type),
                            ...(isStickyFirstColumn(stickyFirstColumn, i, meta.chartType) &&
                              subIdx === 0 &&
                              STICKY_FIRST_COLUMN_STYLES),
                            ...(subCol.customHeaderStyles ? subCol.customHeaderStyles : {}),
                          }}
                        >
                          {subCol.renderHeader ? subCol.renderHeader(subCol) : subCol.label}
                        </TableCell>
                      );
                    }),
                  )}
                </TableRow>
              )}
            </TableHead>
          )}
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={(actions?.length || 0) + (getLeafColumns?.length || 0)}
                  align="center"
                >
                  {Array.from(new Array(loaderRowCount)).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      width="100%"
                      height="1rem"
                      sx={{
                        borderRadius: '0.25rem',
                        marginBottom: '0.5rem',
                      }}
                    />
                  ))}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    actions?.length ? getLeafColumns.length + actions.length : getLeafColumns.length
                  }
                  align="center"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      py: 1,
                    }}
                  >
                    <img src={noDataIcon} alt="no-data" style={{ height: '80px', width: '80px' }} />
                    <Typography fontSize={'0.875rem'} fontWeight={500} sx={{ color: '#6C6C6C' }}>
                      No data available
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  const isPopoverOpen = Boolean(popoverAnchor);
  const rowEditMenu = rowEditMenuAnchor && renderRowEditMenu ? renderRowEditMenu() : null;

  return (
    <>
      <TableContainer
        ref={tableContainerRef}
        style={{
          width: '100%',
          height: '100%',
          marginBottom: '12px',
          maxHeight: isHeightRestricted ? '35rem' : 'none',
          ...(isHeightRestricted ? { overflow: 'auto' } : {}),
          ...(stickyFirstColumn ? { overflowX: 'auto' } : {}),
        }}
      >
        <Table
          stickyHeader={isHeightRestricted && isHeaderVisible}
          sx={{
            borderSpacing: '0 0',
            borderCollapse: 'separate',
          }}
        >
          {isHeaderVisible && (
            <TableHead>
              <TableRow sx={{ borderRadius: '20px' }}>
                {meta.chartType === 'MULTI_LEVEL_TABLE' && (
                  <TableCell
                    rowSpan={hasSecondaryColumns ? 2 : 1}
                    sx={{
                      padding: 0,
                      paddingLeft: depth === 1 ? '0px' : '5px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      lineHeight: '1rem',
                      borderBottom: 'none',
                      background: '#2F736E',
                      borderTopLeftRadius: '8px',
                      WebkitBorderBottomLeftRadius: '8px',
                      paddingRight: depth === 1 ? '0px' : '1rem',
                    }}
                  >
                    <button
                      onClick={handleExpandAllRows}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        color: '#6C6C6C',
                      }}
                    >
                      <ArrowDropDownIcon
                        style={{
                          color: '#9AD6D1',
                          rotate: allExpanded ? '0deg' : '-90deg',
                        }}
                      />
                    </button>
                  </TableCell>
                )}
                {columns?.map((column, i) => (
                  <TableCell
                    key={String(column.key)}
                    colSpan={column.secondaryColumns?.length || 1}
                    rowSpan={column.secondaryColumns?.length ? 1 : hasSecondaryColumns ? 2 : 1}
                    sx={{
                      paddingY: '0.75rem',
                      paddingLeft: '0.5rem',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      lineHeight: '1rem',
                      color: '#F6F8F9',
                      borderLeft: '1px solid #737F86',
                      borderBottom: 'none',
                      background: '#2F736E',
                      borderTopRightRadius:
                        i === columns.length - 1 && !isShowActionColumn ? '8px' : '0px',
                      borderBottomRightRadius:
                        i === columns.length - 1 && !isShowActionColumn && !hasSecondaryColumns
                          ? '8px'
                          : '0px',
                      borderTopLeftRadius:
                        meta.chartType !== 'MULTI_LEVEL_TABLE' && i === 0 ? '8px' : '0px',
                      WebkitBorderBottomLeftRadius:
                        meta.chartType !== 'MULTI_LEVEL_TABLE' && i === 0 ? '8px' : '0px',
                      textAlign: getCellValueAllignment(columnMetadata[column.key]?.type),
                      ...(isStickyFirstColumn(stickyFirstColumn, i, meta.chartType) &&
                        STICKY_FIRST_COLUMN_STYLES),
                      ...(column.customHeaderStyles ? column.customHeaderStyles : {}),
                    }}
                  >
                    {column.renderHeader ? column.renderHeader(column) : column.label}
                  </TableCell>
                ))}
                {isShowActionColumn && (
                  <TableCell
                    rowSpan={hasSecondaryColumns ? 2 : 1}
                    sx={{
                      paddingY: '0.75rem',
                      paddingLeft: '0.5rem',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      lineHeight: '1rem',
                      color: '#F6F8F9',
                      borderLeft: '1px solid #737F86',
                      borderBottom: 'none',
                      background: '#2F736E',
                      borderTopRightRadius: '8px',
                      borderBottomRightRadius: '8px',
                      textAlign: alignActionsColumn,
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
              {/* Second header row for secondary columns */}
              {hasSecondaryColumns && (
                <TableRow>
                  {columns?.map((column, i) =>
                    column.secondaryColumns?.map((subCol, subIdx) => {
                      const isLastSubCol =
                        i === columns.length - 1 && subIdx === column.secondaryColumns!.length - 1;
                      return (
                        <TableCell
                          key={String(subCol.key)}
                          sx={{
                            paddingY: '0.75rem',
                            paddingLeft: '0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            lineHeight: '1rem',
                            color: '#F6F8F9',
                            borderLeft: '1px solid #737F86',
                            borderBottom: 'none',
                            background: '#2F736E',
                            borderTop: '1px solid #737F86',
                            borderBottomRightRadius:
                              isLastSubCol && !isShowActionColumn ? '8px' : '0px',
                            textAlign: getCellValueAllignment(columnMetadata[subCol.key]?.type),
                            ...(isStickyFirstColumn(stickyFirstColumn, i, meta.chartType) &&
                              subIdx === 0 &&
                              STICKY_FIRST_COLUMN_STYLES),
                            ...(subCol.customHeaderStyles ? subCol.customHeaderStyles : {}),
                          }}
                        >
                          {subCol.renderHeader ? subCol.renderHeader(subCol) : subCol.label}
                        </TableCell>
                      );
                    }),
                  )}
                </TableRow>
              )}
            </TableHead>
          )}
          <TableBody
            sx={{
              '&::before': {
                content: '""',
                display: 'block',
                height: '10px',
              },
            }}
          >
            {renderRows(data)}
          </TableBody>
        </Table>
      </TableContainer>
      {renderCellPopover && (
        <Popover
          open={isPopoverOpen}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={popoverPosition.anchorOrigin}
          transformOrigin={popoverPosition.transformOrigin}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 0,
                boxShadow: 'none',
                backgroundColor: 'transparent',
                overflow: 'visible',
              },
            },
          }}
        >
          {popoverData.row &&
            popoverData.columnKey &&
            renderCellPopover(popoverData.cellData, popoverData.row, popoverData.columnKey)}
        </Popover>
      )}
      {rowEditMenuAnchor && rowEditMenu && onCloseRowEditMenu && (
        <Popover
          open={Boolean(rowEditMenuAnchor)}
          anchorEl={rowEditMenuAnchor}
          onClose={onCloseRowEditMenu}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '8px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                width: '209px',
              },
            },
          }}
        >
          {rowEditMenu}
        </Popover>
      )}
    </>
  );
}

export default ImmutableDataTable;
